import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Alert } from '@mui/joy';
import { ArrowLeft } from '@phosphor-icons/react';
import { OpportunityDetail } from '../../components/crm/OpportunityDetail';
import OpportunityModal from '../../components/crm/opportunities/OpportunityModal';
import { api } from '../../lib/api';
import type { OpportunityDetail as OpportunityDetailType, Opportunity } from '../../types';

export const OpportunityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState<OpportunityDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadOpportunity();
    }
  }, [id]);

  const loadOpportunity = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<OpportunityDetailType>(`/api/crm/opportunities/${id}/`);
      setOpportunity(response.data);
    } catch (err: any) {
      console.error('Failed to load opportunity:', err);
      setError(err?.response?.data?.detail || 'Failed to load opportunity details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/crm/pipeline');
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleSaveOpportunity = async (opportunityData: Partial<Opportunity>) => {
    try {
      if (opportunity?.id) {
        await api.updateOpportunity(opportunity.id, opportunityData);
        await loadOpportunity(); // Reload to get updated data
        setShowEditModal(false);
      }
    } catch (error) {
      console.error('Failed to save opportunity:', error);
      throw error;
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleNoteAdded = () => {
    // Reload opportunity to get updated notes
    loadOpportunity();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !opportunity) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          variant="plain"
          color="neutral"
          startDecorator={<ArrowLeft size={16} />}
          onClick={handleBack}
          sx={{ mb: 3 }}
        >
          Back to Pipeline
        </Button>
        <Alert color="danger">
          {error || 'Opportunity not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <OpportunityDetail
        opportunity={opportunity}
        loading={loading}
        onBack={handleBack}
        onEdit={handleEdit}
        onNoteAdded={handleNoteAdded}
      />

      {/* Edit Opportunity Modal */}
      {showEditModal && opportunity && (
        <OpportunityModal
          open={showEditModal}
          onClose={handleCloseEditModal}
          contactId={opportunity.contact || 0}
          opportunity={opportunity as any} // OpportunityDetailType is compatible with Opportunity
          boardId={opportunity.board || undefined}
          initialStatusId={opportunity.board_status || undefined}
          onSaveOpportunity={handleSaveOpportunity}
        />
      )}
    </Box>
  );
};
